import type { FeToolParameter } from "@/services/agent";
import { defaultFeToolDebugParams } from "../feToolDebugPresets";

export function buildInitialFeToolParams(
  toolId: string,
  tool?: { parameters: FeToolParameter[] },
): Record<string, unknown> {
  const result: Record<string, unknown> = { ...defaultFeToolDebugParams(toolId) };
  if (!tool) {
    return result;
  }
  for (const param of tool.parameters) {
    if (param.name in result) {
      continue;
    }
    if (param.type === "boolean") {
      result[param.name] = false;
    } else if (param.enum && param.enum.length > 0) {
      result[param.name] = param.enum[0];
    } else if (param.type === "object" || param.type === "array") {
      result[param.name] = param.type === "array" ? [] : {};
    } else if (param.required) {
      result[param.name] = param.type === "number" ? 0 : "";
    }
  }
  return result;
}

export function formatFeToolParamsJson(params: Record<string, unknown>): string {
  return JSON.stringify(params, null, 2);
}

export function parseFeToolParamsJson(
  text: string,
): { ok: true; value: Record<string, unknown> } | { ok: false; message: string } {
  const trimmed = text.trim();
  if (!trimmed) {
    return { ok: true, value: {} };
  }
  try {
    const value = JSON.parse(trimmed) as unknown;
    if (value === null || typeof value !== "object" || Array.isArray(value)) {
      return { ok: false, message: "Params must be a JSON object" };
    }
    return { ok: true, value: value as Record<string, unknown> };
  } catch {
    return { ok: false, message: "Invalid JSON — fix params before running" };
  }
}

/** Drop empty optional strings and nested empty objects for tool execution. */
export function compactFeToolParams(params: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) {
      continue;
    }
    if (typeof value === "string" && value.trim() === "") {
      continue;
    }
    if (typeof value === "object" && !Array.isArray(value) && Object.keys(value as object).length === 0) {
      continue;
    }
    out[key] = value;
  }
  return out;
}
